import { Injectable, inject, signal } from '@angular/core';
import { CoreService, InMemoryCacheService, UtilsService, User } from 'src/app/common';
import { environment } from 'src/environments/environment';
import { firstValueFrom } from 'rxjs';
import { Invoice, OrganizationSeatUsage } from '../models/billing.model';
import { BillingService } from '../../billing/billing.service';
import { ProductPrices } from 'src/app/common/models/product-prices';
import { BillingProduct } from '../../billing/models/billing-product';

@Injectable({
  providedIn: 'root'
})
export class OrganizationBillingService {

    private core = inject(CoreService<{}>);
    private utils = inject(UtilsService);
    private readonly cache = inject(InMemoryCacheService);
    private readonly billingService = inject(BillingService);
    private identityBaseUrl = environment.identityUrl;

    public user!: User;

    // Signals/state
    private _billingProducts = signal<BillingProduct[]>([]);
    public billingProducts = this._billingProducts.asReadonly();

    private _orgSeatUsage = signal<OrganizationSeatUsage>(null);
    public orgSeatUsage = this._orgSeatUsage.asReadonly();

    private _currentSeatQuantity = signal<number>(0);
    public currentSeatQuantity = this._currentSeatQuantity.asReadonly();

    private _addOnSeats = signal<number>(0);
    public addOnSeats = this._addOnSeats.asReadonly();

    private _invoices = signal<Invoice[]>([]);
    public invoices = this._invoices.asReadonly();

    private _creditCardDetails = signal<any>(null);
    public creditCardDetails = this._creditCardDetails.asReadonly();

    private _productPrices = signal<ProductPrices[]>([]);
    public productPrices = this._productPrices.asReadonly();

    private _isSeatSyncInProgress = signal<boolean>(false);
    public isSeatSyncInProgress = this._isSeatSyncInProgress.asReadonly();

    private _pendingSeatTarget = signal<number | null>(null);
    public pendingSeatTarget = this._pendingSeatTarget.asReadonly();

    private _addOnSeatsInitialized = false;
    private _billingDataOrgId = signal<number | null>(null);
    private _billingDataLoaded = signal<boolean>(false);

    constructor() {}

    /**
     * Get the organization's seat usage overview (included, total, used, available)
     * @param organizationId number or string ID of the organization
     * @returns A Promise resolving to the seat usage info or null on error
     */
    public async getSeatUsage(organizationId: number | string): Promise<OrganizationSeatUsage | null> {
        if (!organizationId) {
            this.utils.logger.error('getSeatUsage() No organizationId provided.');
            return null;
        }
        try {
            const usage = await this.fetchSeatUsageSnapshot(organizationId);
            if (!usage) {
                return null;
            }
            this.applySeatUsage(usage);
            return usage;
        } catch (error) {
            this.utils.logger.error('getSeatUsage() error:', error);
            return null;
        }
    }

    public async getBillingProducts(force = false): Promise<void> {
        try {
            if (force || !this.cache?.billingProducts) {
                await this.cache?.getBillingProducts(false, force);
            }

            const billingProducts = this.cache?.billingProducts?.filter(p =>
                ['seo', 'businessCard', 'askKevin', 'localSeo', 'reviews', 'brandMonitoring'].includes(p?.feature)
            );

            this._billingProducts.set(billingProducts ?? []);
            this.utils.logger.info('Billing Products loaded:', billingProducts);
        } catch (err) {
            this.utils.logger.error('checkBillingProducts() unexpected error:', err);
        }
    }

    public setAddOnSeats(value: number): void {
        // Dont wait for API call to return to update the UI, optimistically update the seat quantity
        this._orgSeatUsage.update(usage => {
            if (!usage) return usage;
            const newTotalSeats = Math.max(0, value);
            return { ...usage, totalSeats: newTotalSeats };
        });
        this._addOnSeats.set(value);
    }

    public beginSeatSync(expectedTotalSeats: number): void {
        const normalizedTarget = Math.max(0, Number(expectedTotalSeats ?? 0));
        this._isSeatSyncInProgress.set(true);
        this._pendingSeatTarget.set(normalizedTarget);
        this._currentSeatQuantity.set(normalizedTarget);
        this._addOnSeats.set(normalizedTarget);

        this._orgSeatUsage.update(usage => {
            if (!usage) {
                return usage;
            }
            return {
                ...usage,
                totalSeats: normalizedTarget,
                availableSeats: Math.max(0, normalizedTarget - Number(usage.usedSeats ?? 0))
            };
        });
    }

    public endSeatSync(): void {
        this._isSeatSyncInProgress.set(false);
        this._pendingSeatTarget.set(null);
    }

    public async waitForSeatUsageSync(
        organizationId: number | string,
        expectedTotalSeats: number,
        maxAttempts: number = 20,
        intervalMs: number = 1500
    ): Promise<boolean> {
        const normalizedExpected = Math.max(0, Number(expectedTotalSeats ?? 0));
        const attempts = Math.max(1, Number(maxAttempts ?? 1));
        const waitMs = Math.max(250, Number(intervalMs ?? 250));

        for (let attempt = 1; attempt <= attempts; attempt++) {
            const usage = await this.fetchSeatUsageSnapshot(organizationId);
            if (usage && Math.max(0, Number(usage.totalSeats ?? 0)) === normalizedExpected) {
                this.applySeatUsage(usage, true);
                this.endSeatSync();
                return true;
            }

            if (attempt < attempts) {
                await this.delay(waitMs);
            }
        }

        this.endSeatSync();
        return false;
    }

    public async fetchInvoices(): Promise<void> {
        const organizationBillingId = this.cache.auth?.organization?.stripeCustomerId || this.cache.auth?.organization?.billingId;
        if (!this.cache.auth?.token || !organizationBillingId) return;

        this.utils.logger.info('Fetching invoices...');
        try {
            const { data, error } = await this.billingService.getUserInvoices(organizationBillingId, false);
            if (error) {
                this.utils.handleApiError(error, 'Failed to fetch user invoice');
                return;
            }

            this._invoices.set(data ?? []);
            this.utils.logger.info('User invoices fetched successfully', this._invoices());
        } catch (err) {
            this.utils.logger.error('fetchInvoices() unexpected error:', err);
        }
    }

    public async fetchCardInfo(): Promise<void> {

        this.user = this.cache.auth?.user?.user
        if (
            !this.cache.auth?.token ||
            !this.user?.billingId ||
            !this.user?.paymentMethodId
        ) {
            return;
        }

        this.utils.logger.info('fetchCardInfo() - Getting user card info');
        try {
            const { data, error } = await this.billingService.getCardInfo(this.user.paymentMethodId);
            if (error) {
                this.utils.handleApiError(error, 'Failed to fetch card information');
                return;
            }
            this._creditCardDetails.set(data);
            this.utils.logger.info('Card information fetched successfully', this._creditCardDetails());
        } catch (err) {
            this.utils.logger.error('fetchCardInfo() unexpected error:', err);
        } finally {
            this.cache.loading = false;
        }
    }

    public async getProductWithPrices(): Promise<void> {

        this.user = this.cache.auth?.user?.user

        if (!this.user?.id) return;

        try {
            const { data, error } = await this.billingService.getProductWithPrices(this.user.id);
            if (error) {
                this.utils.logger.error('BillingComponent.getProductWithPrices', error);
                return;
            }
            this._productPrices.set((data ?? []).sort(
                (a, b) => Number(a.metadata?.sortOrder ?? 0) - Number(b.metadata?.sortOrder ?? 0)
            ));
            this.utils.logger.info('Product with prices loaded:', this.productPrices);
        } catch (err) {
            this.utils.logger.error('BillingComponent.getProductWithPrices unexpected error', err);
        }
    }

    private async fetchSeatUsageSnapshot(organizationId: number | string): Promise<OrganizationSeatUsage | null> {
        const url = `${this.identityBaseUrl}/Seat/${organizationId}/usage`;
        this.utils.logger.info('fetchSeatUsageSnapshot() API URL:', url);
        const seatUsageData = await firstValueFrom(this.core.get(url));

        const usage = (seatUsageData as OrganizationSeatUsage) ?? null;
        if (!usage) {
            return null;
        }

        const usedSeats = Math.max(0, Number(usage.usedSeats ?? 0));
        const totalSeats = Math.max(0, Number(usage.totalSeats ?? 0));
        const normalizedUsage: OrganizationSeatUsage = {
            ...usage,
            // Compatibility field: purchased member capacity excludes the organization owner.
            includedSeats: totalSeats,
            usedSeats,
            totalSeats,
            availableSeats: Math.max(0, totalSeats - usedSeats)
        };

        this.utils.logger.info('fetchSeatUsageSnapshot() success:', normalizedUsage);
        return normalizedUsage;
    }

    private applySeatUsage(usage: OrganizationSeatUsage, resetAddOnSeats: boolean = false): void {
        this._orgSeatUsage.set(usage);
        const currentSeatQuantity = Math.max(0, Number(usage?.totalSeats ?? 0));
        this._currentSeatQuantity.set(currentSeatQuantity);

        if (resetAddOnSeats || !this._addOnSeatsInitialized) {
            this._addOnSeats.set(currentSeatQuantity);
            this._addOnSeatsInitialized = true;
        }
    }

    private async delay(ms: number): Promise<void> {
        await new Promise(resolve => setTimeout(resolve, ms));
    }

    public async loadBillingData(orgId: number, force = false): Promise<void> {
        const hasBillingDataForOrg = this._billingDataLoaded() && this._billingDataOrgId() === orgId;
        if (hasBillingDataForOrg && !force) {
            return;
        }

        try {
            this.utils.logger.info('Reloading billing data...');

            await Promise.all([
                this.getSeatUsage(orgId),
                this.fetchInvoices(),
                this.fetchCardInfo(),
                this.getProductWithPrices(),
                this.getBillingProducts(force)
            ]);

            this._billingDataOrgId.set(orgId);
            this._billingDataLoaded.set(true);
            this.utils.logger.info('Billing data reloaded successfully');
        } catch (err) {
            this.utils.logger.error('loadBillingData() error:', err);
        }
    }
}
